import sqlite3InitModule, {
  type BindableValue,
  type Database,
  type OpfsDatabase,
} from '@sqlite.org/sqlite-wasm';

import { type SqlBackend, type SqlParam } from './sql.js';

/**
 * Browser SqlBackend backed by sqlite-wasm over OPFS sync-access-handles.
 * MUST run inside a dedicated Web Worker (the sync OPFS handle API is
 * forbidden on the main thread). The browser entry in `./browser.ts`
 * spawns the worker and exposes this through Comlink.
 *
 * If OPFS is unavailable (older browser), throws `OpfsUnavailableError`;
 * the caller (browser.ts) falls back to an in-memory backend so the GM
 * can still play a single session (data is lost on reload). The
 * `persistent` flag returned by `openBrowserReplica` lets the UI render
 * a non-dismissable warning banner above the app chrome.
 */
export class OpfsUnavailableError extends Error {
  /**
   * Original message from the swallowed `new OpfsDb(...)` throw, if
   * any. Surfaced by the worker open path on `console.info` and
   * stitched into the "Why?" dialog so we can tell *why* OPFS was
   * rejected on this device (e.g. concurrent handle held by a stale
   * worker, missing COOP/COEP headers, private-mode WebKit). Without
   * this we silently coerce every failure into "unavailable" and
   * dev-tools shows nothing.
   */
  override readonly cause: string | undefined;

  constructor(cause?: string) {
    super(
      cause === undefined
        ? 'OPFS is unavailable in this browser. dungeonlogs requires Chrome 102+, Safari 16.4+, or Firefox 111+.'
        : `OPFS is unavailable in this browser: ${cause}`,
    );
    this.name = 'OpfsUnavailableError';
    this.cause = cause;
  }
}

export async function createBrowserBackend(filename = '/dungeonlogs.db'): Promise<SqlBackend> {
  const sqlite3 = await sqlite3InitModule();
  // The OpfsDb constructor throws if OPFS isn't usable in this isolate.
  // Carry the original error message forward — the bare-`catch` shape
  // that shipped initially silently coerced every cause (browser
  // missing OPFS, concurrent sync-access-handle, security headers
  // misconfigured) into the same generic "OPFS is unavailable"
  // message. The downstream banner then reported "OPFS didn't open"
  // even on transient failures where a remount would have succeeded.
  let db: Database;
  try {
    db = new sqlite3.oo1.OpfsDb(filename, 'c') as OpfsDatabase;
  } catch (err) {
    const cause = err instanceof Error ? err.message : String(err);
    throw new OpfsUnavailableError(cause);
  }
  return new BrowserBackend(db);
}

/**
 * In-memory browser backend: sqlite-wasm against `:memory:`. Used as a
 * fallback when OPFS is unavailable so the substrate is still usable for
 * a single session. The schema, op-log, and SyncClient round-trips all
 * work identically — the only loss is persistence across reloads.
 *
 * The op-log invariant is preserved: appends still flow through the same
 * Replica wrapper; current-value tables are still caches. The DOSQL
 * server-side log remains the canonical record, so persistent state
 * survives in the workspace DO even when the local replica is volatile.
 *
 * Distinct from `createNodeBackend(':memory:')` only by call site —
 * the underlying class is the same; we keep a thin wrapper here so the
 * dispatch in `browser.ts` doesn't have to import a node-named module
 * from a browser bundle. Vite/rollup tree-shaking happens at the
 * createNodeBackend call site, not here.
 */
export async function createBrowserMemoryBackend(): Promise<SqlBackend> {
  const sqlite3 = await sqlite3InitModule();
  const db = new sqlite3.oo1.DB(':memory:', 'c');
  return new BrowserBackend(db);
}

class BrowserBackend implements SqlBackend {
  constructor(private readonly db: Database) {}

  exec(sql: string, ...params: SqlParam[]): void {
    if (params.length === 0) {
      this.db.exec(sql);
      return;
    }
    this.db.exec({ sql, bind: params as BindableValue[] });
  }

  all<Row extends Record<string, unknown>>(sql: string, ...params: SqlParam[]): Row[] {
    const rows: Row[] = [];
    this.db.exec({
      sql,
      bind: params as BindableValue[],
      rowMode: 'object',
      callback: (row: unknown) => {
        rows.push(row as Row);
      },
    });
    return rows;
  }

  one<Row extends Record<string, unknown>>(sql: string, ...params: SqlParam[]): Row | null {
    const rows = this.all<Row>(sql, ...params);
    return rows[0] ?? null;
  }

  close(): void {
    this.db.close();
  }
}
